import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from './components/AppLayout';
import { getDemoAuth } from '@/hooks/useAppAuth';
import { notices } from '@/mocks/notices';
import KakaoTalkButton from '@/components/feature/KakaoTalkButton';

interface Profile {
  name: string;
  roles: string[];
  avatar_url?: string;
}

interface PatientProfile {
  id?: string;
}

interface CaregiverProfile {
  is_active?: boolean;
  experience_years?: number;
  hourly_rate?: number;
  rating_avg?: number;
}

interface DashboardMenuItem {
  id: string;
  label: string;
  desc: string;
  icon: string;
  color: string;
  bg: string;
  path: string;
  badge?: string;
}

export default function AppDashboardPage() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [profile, setProfile] = useState<Profile | null>(null);
  const [patientProfile, setPatientProfile] = useState<PatientProfile | null>(null);
  const [caregiverProfile, setCaregiverProfile] = useState<CaregiverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [showNotices, setShowNotices] = useState(false);
  const [showPatientRequiredModal, setShowPatientRequiredModal] = useState(false);
  const [showCaregiverRequiredModal, setShowCaregiverRequiredModal] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      // Check real session first
      const { data: { session } } = await supabase.auth.getSession();
      let uid = session?.user?.id || null;

      // Fallback to demo auth
      if (!uid) {
        const demo = getDemoAuth();
        uid = demo?.userId || null;
      }

      if (!uid) {
        setLoading(false);
        return;
      }

      setUserId(uid);

      // Load profile
      const { data: p } = await supabase
        .from('profiles')
        .select('name, roles, avatar_url')
        .eq('id', uid)
        .maybeSingle();

      if (p) {
        const resolvedRoles: string[] = p.roles?.length ? p.roles : [];
        setProfile({ name: p.name, roles: resolvedRoles, avatar_url: p.avatar_url });
      }

      // Check patient profile
      const { data: pp } = await supabase
        .from('patient_profiles')
        .select('id')
        .eq('user_id', uid)
        .maybeSingle();
      if (pp) setPatientProfile(pp);

      // Check caregiver profile
      const { data: cp } = await supabase
        .from('caregiver_profiles')
        .select('is_active, experience_years, hourly_rate, rating_avg')
        .eq('user_id', uid)
        .maybeSingle();
      if (cp) setCaregiverProfile(cp);

      setLoading(false);
    }
    checkAuth();
  }, []);

  // Build dashboard menu items
  const menuItems: DashboardMenuItem[] = [
    {
      id: 'register-patient',
      label: '환자 등록',
      desc: patientProfile ? '환자 정보 수정' : '간병이 필요한 환자 등록',
      icon: 'ri-hospital-line',
      color: '#2E7D6B',
      bg: 'bg-[#E8F5E9]',
      path: '/app/register-patient',
      badge: patientProfile ? '등록완료' : undefined,
    },
    {
      id: 'care-matching',
      label: '간병매칭신청',
      desc: '조건에 맞는 간병인과 매칭 신청',
      icon: 'ri-hand-heart-line',
      color: '#E65100',
      bg: 'bg-[#FFF8E1]',
      path: '/app/care-request',
    },
    {
      id: 'find-caregiver',
      label: '간병인 찾기',
      desc: '지역·경력·시급별 간병인 검색',
      icon: 'ri-search-line',
      color: '#F57C00',
      bg: 'bg-[#FFF3E0]',
      path: '/app/patient/search',
    },
    {
      id: 'register-caregiver',
      label: '간병인 등록',
      desc: caregiverProfile ? '간병인 정보 수정' : '간병인으로 활동 등록',
      icon: 'ri-nurse-line',
      color: '#1976D2',
      bg: 'bg-[#E3F2FD]',
      path: '/app/register-caregiver',
      badge: caregiverProfile ? '등록완료' : undefined,
    },
    {
      id: 'find-patient',
      label: '환자 찾기',
      desc: '간병이 필요한 환자 검색',
      icon: 'ri-user-search-line',
      color: '#7B1FA2',
      bg: 'bg-[#F3E5F5]',
      path: '/app/caregiver/search',
    },
    {
      id: 'insurance',
      label: '보험서류 요청',
      desc: '간병 관련 보험서류 발급',
      icon: 'ri-file-shield-2-line',
      color: '#009688',
      bg: 'bg-[#E0F2F1]',
      path: '/app/insurance-doc',
    },
  ];

  async function handleKakaoLogin() {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: 'https://koreacarers.com/app/auth/callback',
      },
    });
    if (oauthError) {
      console.error('Kakao OAuth error:', oauthError);
    }
  }

  // Not logged in state
  if (!loading && !userId) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 pb-24">
          <div className="text-center mb-14">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5">
              <img
                src="https://storage.readdy-site.link/project_files/85651164-9881-44bb-8cd7-002bfdcb405b/ccf5aafb-c3da-4b67-8f64-1b7bcab82f90__smallsize.png?v=7d34dd8d91c49a026e06eeef1279073c"
                alt="코리아케어러스 로고"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-[28px] font-black text-gray-900 leading-tight mb-2">
              코리아케어러스
            </h1>
            <p className="text-sm text-gray-500">
              믿을 수 있는 간병인을 만나보세요
            </p>
          </div>

          <div className="w-full space-y-3 mb-10">
            <button
              onClick={handleKakaoLogin}
              className="w-full py-4 rounded-2xl text-base font-bold text-[#000000] whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center gap-2.5"
              style={{ backgroundColor: '#FEE500' }}
            >
              <i className="ri-kakao-talk-fill text-xl" />
              카카오톡으로 시작하기
            </button>

            <button
              onClick={() => navigateRef.current('/app/login')}
              className="w-full py-4 rounded-2xl text-base font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-all flex items-center justify-center gap-2.5 border-2 border-gray-200 text-gray-800 bg-white hover:border-gray-300"
            >
              <i className="ri-smartphone-line text-lg" />
              휴대폰으로 시작하기
            </button>
          </div>

          <a
            href="/"
            className="text-xs text-gray-400 underline cursor-pointer hover:text-gray-600 transition-colors"
          >
            회사홈페이지로 돌아가기
          </a>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const hasPatientRole = profile?.roles?.includes('patient');
  const hasCaregiverRole = profile?.roles?.includes('caregiver');
  const patientComplete = hasPatientRole && !!patientProfile;
  const caregiverComplete = hasCaregiverRole && !!caregiverProfile;

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                <i className="ri-user-3-line text-xl text-[#2E7D6B]" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">
                {profile ? `${profile.name}님` : '안녕하세요!'}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                {hasPatientRole && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${patientComplete ? 'bg-[#E8F5E9] text-[#2E7D6B]' : 'bg-gray-100 text-gray-400'}`}>
                    {patientComplete ? '환자정보 완료' : '환자등록 필요'}
                  </span>
                )}
                {hasCaregiverRole && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${caregiverComplete ? 'bg-[#E3F2FD] text-[#1976D2]' : 'bg-gray-100 text-gray-400'}`}>
                    {caregiverComplete ? '간병인정보 완료' : '간병인등록 필요'}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate('/app/profile')}
              className="ml-auto w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <i className="ri-settings-3-line text-gray-400" />
            </button>
          </div>
        </div>

        {/* 등록 유도 배너 (둘 다 미완료 시) */}
        {((hasPatientRole && !patientComplete) || (hasCaregiverRole && !caregiverComplete)) && (
          <div className="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800 mb-1">추가 정보 등록이 필요합니다</p>
                <p className="text-xs text-amber-600 leading-relaxed">
                  원활한 매칭을 위해{' '}
                  {hasPatientRole && !patientComplete && '환자 상세정보'}
                  {hasPatientRole && !patientComplete && hasCaregiverRole && !caregiverComplete && '와 '}
                  {hasCaregiverRole && !caregiverComplete && '간병인 상세정보'}
                  {' '}등록을 완료해주세요.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 역할별 요약 카드 */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {hasPatientRole && patientProfile && (
            <button
              onClick={() => navigate('/app/register-patient')}
              className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                  <i className="ri-hospital-line text-sm text-[#2E7D6B]" />
                </div>
                <span className="text-xs font-bold text-[#2E7D6B]">환자 정보</span>
              </div>
              <p className="text-xs text-gray-500">프로필 등록 완료</p>
            </button>
          )}
          {hasCaregiverRole && caregiverProfile && (
            <button
              onClick={() => navigate('/app/register-caregiver')}
              className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                  <i className="ri-nurse-line text-sm text-[#1976D2]" />
                </div>
                <span className="text-xs font-bold text-[#1976D2]">간병인 정보</span>
              </div>
              <div className="text-xs text-gray-500">
                {caregiverProfile.experience_years ? `경력 ${caregiverProfile.experience_years}년` : ''}
                {caregiverProfile.hourly_rate ? ` · ${caregiverProfile.hourly_rate.toLocaleString()}원` : ''}
              </div>
              {caregiverProfile.rating_avg != null && caregiverProfile.rating_avg > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <i className="ri-star-fill text-xs text-amber-400" />
                  <span className="text-xs font-bold text-gray-700">{caregiverProfile.rating_avg}</span>
                </div>
              )}
            </button>
          )}
        </div>

        {/* 메인 메뉴 그리드 (3x2) */}
        <h2 className="text-sm font-extrabold text-gray-900 mb-3">서비스 메뉴</h2>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === 'find-caregiver' && !patientProfile) {
                  setShowPatientRequiredModal(true);
                  return;
                }
                if (item.id === 'care-matching' && !patientProfile) {
                  setShowPatientRequiredModal(true);
                  return;
                }
                if (item.id === 'find-patient' && !caregiverProfile) {
                  setShowCaregiverRequiredModal(true);
                  return;
                }
                if (item.path.startsWith('/app')) {
                  navigate(item.path);
                } else {
                  window.open(item.path, '_blank');
                }
              }}
              className="bg-white rounded-2xl border border-gray-100 p-4 text-center cursor-pointer hover:shadow-sm transition-all active:scale-[0.97] relative"
            >
              {item.badge && (
                <span className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-[#E8F5E9] text-[#2E7D6B] font-bold">
                  {item.badge}
                </span>
              )}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-2.5 ${item.bg}`}>
                <i className={`${item.icon} text-lg`} style={{ color: item.color }} />
              </div>
              <h3 className="text-xs font-bold text-gray-900 mb-1 whitespace-nowrap">{item.label}</h3>
              <p className="text-[10px] text-gray-400 leading-tight">{item.desc}</p>
            </button>
          ))}
        </div>

        {/* 가족간병 신청 - 별도 섹션 */}
        <div className="mb-5 rounded-2xl border-2 border-[#FCE4EC] bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-[#FCE4EC] to-[#F8BBD0]/30 px-5 py-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#E91E63]/10 flex items-center justify-center">
              <i className="ri-user-heart-line text-sm text-[#E91E63]" />
            </div>
            <span className="text-xs font-extrabold text-[#E91E63]">특별 서비스</span>
          </div>
          <button
            onClick={() => navigate('/app/family-care')}
            className="w-full p-5 text-left cursor-pointer hover:bg-[#FCE4EC]/30 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-extrabold text-gray-900 mb-1">가족간병 신청</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  가족 구성원을 위한 맞춤 간병 서비스를 신청하세요
                </p>
              </div>
              <div className="w-9 h-9 rounded-full bg-[#FCE4EC] flex items-center justify-center shrink-0">
                <i className="ri-arrow-right-s-line text-[#E91E63]" />
              </div>
            </div>
          </button>
        </div>

        {/* 전화 문의 배너 */}
        <a
          href="tel:15668654"
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 mb-3 cursor-pointer hover:shadow-sm transition-all"
        >
          <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
            <i className="ri-phone-line text-2xl text-[#2E7D6B]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">전화 문의</p>
            <p className="text-xs text-gray-400">1566-8654</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            <i className="ri-arrow-right-s-line text-gray-400" />
          </div>
        </a>

        {/* 카톡 문의 배너 */}
        <KakaoTalkButton
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 mb-5 cursor-pointer hover:shadow-sm transition-all"
          ariaLabel="카카오톡 상담 문의"
        >
          <div className="w-12 h-12 rounded-xl bg-[#FFF8E1] flex items-center justify-center shrink-0">
            <i className="ri-kakao-talk-fill text-2xl text-[#3C1E1E]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">카톡 상담</p>
            <p className="text-xs text-gray-400">카카오톡으로 빠르게 문의</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            <i className="ri-arrow-right-s-line text-gray-400" />
          </div>
        </KakaoTalkButton>

        {/* 채팅 바로가기 */}
        <button
          onClick={() => navigate('/app/chat-list')}
          className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 mb-5 cursor-pointer hover:shadow-sm transition-all"
        >
          <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0 relative">
            <i className="ri-chat-3-line text-2xl text-[#2E7D6B]" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-bold text-gray-900">1:1 채팅</p>
            <p className="text-xs text-gray-400">매칭된 상대방과 실시간으로 대화하세요</p>
          </div>
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            <i className="ri-arrow-right-s-line text-gray-400" />
          </div>
        </button>

        {/* 서비스 이용 안내 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
          <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-lightbulb-flash-line text-amber-500 text-lg" />
            서비스 이용 안내
          </h2>
          <div className="space-y-4">
            {[
              {
                step: '01',
                title: '회원가입 후 상세정보 등록',
                desc: '기본 정보 가입 후 환자 또는 간병인 상세 정보를 등록하면 매칭이 시작됩니다.',
                icon: 'ri-user-add-line',
              },
              {
                step: '02',
                title: '조건에 맞는 상대방 검색',
                desc: '지역, 경력, 비용 등 조건으로 원하는 간병인 또는 환자를 찾아보세요.',
                icon: 'ri-search-line',
              },
              {
                step: '03',
                title: '매칭 신청 및 진행',
                desc: '서로 수락하면 연락처가 공개되고, 간병이 시작됩니다. 간병비는 당사자 간 직접 정산합니다.',
                icon: 'ri-exchange-funds-line',
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#f0f9f6] flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-sm text-[#2E7D6B]`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-extrabold text-[#2E7D6B] bg-[#f0f9f6] px-1.5 py-0.5 rounded-md">
                      {item.step}
                    </span>
                    <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 공지사항 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
              <i className="ri-megaphone-line text-[#2E7D6B] text-lg" />
              공지사항
            </h2>
            <button
              onClick={() => setShowNotices(true)}
              className="text-xs text-[#2E7D6B] font-bold cursor-pointer hover:underline"
            >
              더보기
            </button>
          </div>
          <div className="space-y-3">
            {notices.slice(0, 3).map((n) => (
              <div key={n.id} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#2E7D6B] mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-medium truncate">{n.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-400">{n.date}</span>
                    {n.isNew && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-500 font-bold">
                        NEW
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 공지사항 전체 모달 */}
        {showNotices && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={() => setShowNotices(false)}>
            <div
              className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-slide-up max-h-[90vh] md:max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">공지사항</h2>
                <button onClick={() => setShowNotices(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {notices.map((n) => (
                  <div key={n.id} className="p-4 rounded-xl bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">
                        {n.category}
                      </span>
                      {n.isNew && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-500 font-bold">
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1">{n.title}</p>
                    <p className="text-xs text-gray-400 mb-2">{n.date}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{n.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 환자 등록 필요 모달 */}
        {showPatientRequiredModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5" onClick={() => setShowPatientRequiredModal(false)}>
            <div
              className="bg-white w-full max-w-sm rounded-2xl p-6 text-center animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-amber-500" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-2">환자 등록이 필요합니다</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                간병인 찾기와 간병매칭신청은 환자 등록을 먼저 완료해야 이용할 수 있어요.<br />
                환자 정보를 등록하고 맞춤 간병인을 찾아보세요.
              </p>
              <div className="space-y-2.5">
                <button
                  onClick={() => {
                    setShowPatientRequiredModal(false);
                    navigate('/app/register-patient');
                  }}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#2E7D6B' }}
                >
                  환자 등록하러 가기
                </button>
                <button
                  onClick={() => setShowPatientRequiredModal(false)}
                  className="w-full py-3 rounded-xl text-sm font-medium text-gray-500 whitespace-nowrap cursor-pointer hover:bg-gray-50 transition-colors bg-gray-100"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 간병인 등록 필요 모달 */}
        {showCaregiverRequiredModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5" onClick={() => setShowCaregiverRequiredModal(false)}>
            <div
              className="bg-white w-full max-w-sm rounded-2xl p-6 text-center animate-scale-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-blue-500" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-2">간병인 등록이 필요합니다</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                환자 찾기는 간병인 등록을 먼저 완료해야 이용할 수 있어요.<br />
                간병인 정보를 등록하고 맞춤 환자를 찾아보세요.
              </p>
              <div className="space-y-2.5">
                <button
                  onClick={() => {
                    setShowCaregiverRequiredModal(false);
                    navigate('/app/register-caregiver');
                  }}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#1976D2' }}
                >
                  간병인 등록하러 가기
                </button>
                <button
                  onClick={() => setShowCaregiverRequiredModal(false)}
                  className="w-full py-3 rounded-xl text-sm font-medium text-gray-500 whitespace-nowrap cursor-pointer hover:bg-gray-50 transition-colors bg-gray-100"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
          @keyframes scale-in {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in {
            animation: scale-in 0.2s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}